// Sekcja "Produkty proponowane" pod sekcją wysyłki w karcie oferty klienta (Panel Handlowca).

import React, { useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { CircularProgress } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import { WarehouseIcon } from 'assets/icons';
import { useGetSalesCrmOfferSuggestedProducts } from 'api';
import AddOfferPositionModal from 'components/containers/TraderClientOfferProductsContainer/AddOfferPositionModal';

import styles from 'theme/components/containers/TraderClientOfferSuggestedProductsContainer/TraderClientOfferSuggestedProductsContainer.module.scss';

interface IProps {
  clientId: number;
  offerId: string;
}

interface ISelectedSuggestion {
  productId: number;
  searchKeyword: string;
}

const formatPrice = (raw: number | null): string => {
  if (raw == null || Number.isNaN(raw)) return '-';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

/**
 * Zdjecie produktu z fallbackiem na ikone gdy brak URL lub HTTP 404.
 * Wzorzec WiseProductItem (onError + state).
 */
const SuggestedProductImage: React.FC<{ src: string | null; imgClassName: string; placeholderClassName: string }> = ({
  src,
  imgClassName,
  placeholderClassName
}) => {
  const [hasError, setHasError] = useState(false);
  if (!src || hasError) {
    return (
      <div className={placeholderClassName}>
        <ImageOutlinedIcon fontSize="inherit" />
      </div>
    );
  }
  return <img src={src} alt="" className={imgClassName} onError={() => setHasError(true)} />;
};

const TraderClientOfferSuggestedProductsContainer: React.FC<IProps> = ({ clientId, offerId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useGetSalesCrmOfferSuggestedProducts(clientId, offerId);
  const [selected, setSelected] = useState<ISelectedSuggestion | null>(null);

  const items = data?.items ?? [];

  const handleAdded = () => {
    queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId, 'positions']);
    // Prefiks (bez versionId) - żeby trafić w klucze niezależnie od wersji oferty w cache.
    queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId, 'totals']);
    queryClient.invalidateQueries([
      'sales-crm',
      'client',
      clientId,
      'offer',
      offerId,
      'suggested-products'
    ]);
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientOfferSuggestedProductsContainer'
      )}>
      <h3 className={styles.title}>{t('Produkty proponowane')}</h3>

      {isLoading && (
        <div className={styles.statePlaceholder}>
          <CircularProgress size={24} />
        </div>
      )}

      {!isLoading && isError && (
        <div className={styles.statePlaceholder}>
          {t('Nie udało się pobrać produktów proponowanych.')}
        </div>
      )}

      {!isLoading && !isError && items.length === 0 && (
        <div className={styles.statePlaceholder}>
          {t('Brak produktów proponowanych.')}
        </div>
      )}

      {!isLoading && !isError && items.length > 0 && (
        <div className={styles.grid}>
          {items.map((product) => {
            // Najpewniejsza fraza wyszukiwania to symbol/SKU; fallback do nazwy.
            const searchKeyword =
              (product.symbol && product.symbol.trim().length > 0)
                ? product.symbol
                : (product.productName ?? '');
            return (
              <button
                type="button"
                key={product.productId}
                className={styles.card}
                onClick={() =>
                  setSelected({
                    productId: product.productId,
                    searchKeyword
                  })
                }
                aria-label={t('Dodaj do oferty: {{name}}', {
                  name: product.productName ?? product.symbol ?? `#${product.productId}`
                })}>
                <div className={styles.imageWrapper}>
                  <SuggestedProductImage
                    src={product.imageUrl ?? null}
                    imgClassName={styles.image}
                    placeholderClassName={styles.imagePlaceholder}
                  />
                </div>

                <div className={styles.name}>{product.productName ?? product.symbol ?? '-'}</div>

                <div className={styles.footerRow}>
                  <span className={styles.price}>{formatPrice(product.priceNet)}</span>
                  <span className={styles.qtyBlock}>
                    {product.stockQty}
                    {t('szt.')}
                    <WarehouseIcon className={styles.qtyIcon} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected !== null && (
        <AddOfferPositionModal
          open
          clientId={clientId}
          offerId={offerId}
          initialSearchKeyword={selected.searchKeyword}
          initialProductId={selected.productId}
          onClose={() => setSelected(null)}
          onAdded={handleAdded}
        />
      )}
    </div>
  );
};

export default TraderClientOfferSuggestedProductsContainer;
